/**
 * Consolidated Permissions Hook - Week 1 Optimization
 * Replaces 8+ separate permission queries with single batched call
 * 
 * Performance Target:
 * - 85% reduction in database calls (8 -> 1)
 * - 30% reduction in loading time
 * - Response time < 200ms
 * 
 * Feature Flag: ENABLE_CONSOLIDATED_PERMISSIONS (default: OFF)
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useAdminCheck } from '@/hooks/useAdminCheck';

export interface ConsolidatedPermissions {
  // Original high-level permissions
  isAdmin: boolean;
  canCreateProjects: boolean;
  canAccessItems: boolean;
  canAccessStakeholders: boolean;
  canAccessSubcons: boolean;
  canAccessMaintenance: boolean;
  canAccessProcurement: boolean;
  canAccessDiary: boolean;
  canAccessSpecs: boolean; // Added for specs access
  
  // Detailed permission codes (for adapter mapping)
  // Subcons
  SUBCONS_ACCESS?: boolean;
  SUBCONS_VIEW?: boolean;
  SUBCONS_EDIT?: boolean;
  SUBCONS_MANAGE?: boolean;
  SUBCONS_GLOBAL_VIEW_DATE?: boolean;
  SUBCONS_GLOBAL_VIEW_SUBCONS?: boolean;
  SUBCONS_GLOBAL_VIEW_PO?: boolean;
  SUBCONS_GLOBAL_EXPORT?: boolean;
  
  // Procurement
  PROCUREMENT_MANAGE_CONTRACTORS?: boolean;
  PROCUREMENT_CREATE_ORDER?: boolean;
  PROCUREMENT_EDIT_ORDER?: boolean;
  PROCUREMENT_DELETE_ORDER?: boolean;
  
  // Items
  ITEMS_ACCESS?: boolean;
  ITEMS_VIEW_IN_PROJECT?: boolean;
  ITEMS_EDIT_BASIC?: boolean;
  ITEMS_EDIT_FUNCTIONS?: boolean;
  ITEMS_REORDER?: boolean;
  ITEMS_DELETE?: boolean; // Added
  ITEMS_DUPLICATE?: boolean; // Added
  
  // Specs
  SPECS_ACCESS?: boolean;
  SPECS_ITEMS_VIEW?: boolean;
  SPECS_ITEMS_EDIT?: boolean;
  SPECS_MANAGE_LAYERS?: boolean;
  SPECS_DELETE_PINS?: boolean; // Added
  SPECS_EXPORT?: boolean; // Added
  
  // UI Actions (new)
  EXPORT_DATA?: boolean;
  IMPORT_DATA?: boolean;
  BULK_EDIT?: boolean;
  BULK_DELETE?: boolean;
  
  // Projects (new)
  PROJECTS_SHARE?: boolean;
  PROJECTS_ARCHIVE?: boolean;
  PROJECTS_DUPLICATE?: boolean;
  PROJECTS_SET_MARK?: boolean;
  
  // ✅ NEW: Folder View Permissions (for ENABLE_NEW_GDRIVE_FOLDER_PERMISSIONS)
  VIEW_FOLDER_SPECS?: boolean;
  VIEW_FOLDER_SHOP?: boolean;
  VIEW_FOLDER_PO?: boolean;
  VIEW_FOLDER_DOC?: boolean;
  VIEW_FOLDER_COST?: boolean;
  VIEW_FOLDER_BUILD?: boolean;
  VIEW_FOLDER_DOC_CONTRACT?: boolean;
  
  // ✅ Option B: DOC Subfolder VIEW Permissions
  VIEW_FOLDER_DOC_TECH?: boolean;
  VIEW_FOLDER_DOC_REQ?: boolean;
  VIEW_FOLDER_DOC_REPORT?: boolean;
  VIEW_FOLDER_DOC_PROPOSAL?: boolean;
  
  // ✅ Option B: DOC Subfolder UPLOAD Permissions
  UPLOAD_FOLDER_DOC_TECH?: boolean;
  UPLOAD_FOLDER_DOC_REQ?: boolean;
  UPLOAD_FOLDER_DOC_REPORT?: boolean;
  UPLOAD_FOLDER_DOC_PROPOSAL?: boolean;
  
  // ✅ Option B: DOC Subfolder DELETE Permissions
  DELETE_FOLDER_DOC_TECH?: boolean;
  DELETE_FOLDER_DOC_REQ?: boolean;
  DELETE_FOLDER_DOC_REPORT?: boolean;
  DELETE_FOLDER_DOC_PROPOSAL?: boolean;
  
  // ✅ PERMIT Permissions
  PERMIT_VIEW?: boolean;
  PERMIT_CREATE?: boolean;
  PERMIT_EDIT?: boolean;
  PERMIT_DELETE?: boolean;
  PERMIT_SHARE?: boolean;
  
  // ✅ Navigation Permissions (for Header.tsx)
  COLLECTION_VIEW?: boolean;
  SURVEY_ACCESS?: boolean;
  HR_ACCESS?: boolean;
  
  // ✅ NEW: Additional Navigation Access Flags
  HOME_ACCESS?: boolean;
  SITE_ACCESS?: boolean;
  ROADMAP_ACCESS?: boolean;
  PROJECTS_ACCESS?: boolean;
  
  // ✅ FIX: Explicit permission codes for tabs (avoid RPC OR logic issues)
  STAKEHOLDERS_ACCESS?: boolean;
  MAINTENANCE_ACCESS?: boolean;
  PROCUREMENT_ACCESS?: boolean;
  MEETING_ACCESS?: boolean;
  
  // ✅ NEW: AI Data Source Permissions
  AI_DATA_EXECUTE?: boolean;
  AI_DATA_MEETING?: boolean;
  AI_DATA_SPECS?: boolean;
  AI_DATA_DRAWING?: boolean;
  AI_DATA_PERMIT?: boolean;
  AI_DATA_COLLECTION?: boolean;
  AI_DATA_TEAM?: boolean;
  AI_DATA_CRM?: boolean;
  
  // ✅ NEW: AI File Analyzer Permission
  AI_FILE_ANALYSIS_ACCESS?: boolean;
  
  // ✅ NEW: AI Navigation Permissions
  AI_PROJECTS_ACCESS?: boolean;
  AI_CRM_ACCESS?: boolean;
  
  // ✅ NEW: AI Tab Permissions
  AI_TAB_FINANCE?: boolean;
  AI_TAB_RESOURCES?: boolean;
  
  // ✅ NEW: Library Access
  LIBRARY_ACCESS?: boolean;
  LIBRARY_SPECS_ACCESS?: boolean;
  LIBRARY_KEYSPECS_ACCESS?: boolean;

  // ✅ NEW: Sport Permissions
  SPORT_ACCESS?: boolean;
  SPORT_CREATE_TOURNAMENT?: boolean;
  SPORT_CREATE_TRAINING?: boolean;

  // ✅ NEW: Reports Module Permissions (Session 1 Foundation)
  REPORTS_ACCESS?: boolean;
  REPORTS_VIEW?: boolean;
  REPORTS_CREATE?: boolean;
  REPORTS_ADMIN?: boolean;
}

const DEFAULT_PERMISSIONS: ConsolidatedPermissions = {
  isAdmin: false,
  canCreateProjects: false,
  canAccessItems: false,
  canAccessStakeholders: false,
  canAccessSubcons: false,
  canAccessMaintenance: false,
  canAccessProcurement: false,
  canAccessDiary: false,
  canAccessSpecs: false,
  SUBCONS_ACCESS: false,
  SUBCONS_VIEW: false,
  SUBCONS_EDIT: false,
  SUBCONS_MANAGE: false,
  SUBCONS_GLOBAL_VIEW_DATE: false,
  SUBCONS_GLOBAL_VIEW_SUBCONS: false,
  SUBCONS_GLOBAL_VIEW_PO: false,
  SUBCONS_GLOBAL_EXPORT: false,
  PROCUREMENT_MANAGE_CONTRACTORS: false,
  PROCUREMENT_CREATE_ORDER: false,
  PROCUREMENT_EDIT_ORDER: false,
  PROCUREMENT_DELETE_ORDER: false,
  ITEMS_ACCESS: false,
  ITEMS_VIEW_IN_PROJECT: false,
  ITEMS_EDIT_BASIC: false,
  ITEMS_EDIT_FUNCTIONS: false,
  ITEMS_REORDER: false,
  ITEMS_DELETE: false,
  ITEMS_DUPLICATE: false,
  SPECS_ACCESS: false,
  SPECS_ITEMS_VIEW: false,
  SPECS_ITEMS_EDIT: false,
  SPECS_MANAGE_LAYERS: false,
  SPECS_DELETE_PINS: false,
  SPECS_EXPORT: false,
  EXPORT_DATA: false,
  IMPORT_DATA: false,
  BULK_EDIT: false,
  BULK_DELETE: false,
  PROJECTS_SHARE: false,
  PROJECTS_ARCHIVE: false,
  PROJECTS_DUPLICATE: false,
  PROJECTS_SET_MARK: false,
  // ✅ Folder View Permissions
  VIEW_FOLDER_SPECS: false,
  VIEW_FOLDER_SHOP: false,
  VIEW_FOLDER_PO: false,
  VIEW_FOLDER_DOC: false,
  VIEW_FOLDER_COST: false,
  VIEW_FOLDER_BUILD: false,
  VIEW_FOLDER_DOC_CONTRACT: false,
  VIEW_FOLDER_DOC_TECH: false,
  VIEW_FOLDER_DOC_REQ: false,
  VIEW_FOLDER_DOC_REPORT: false,
  VIEW_FOLDER_DOC_PROPOSAL: false,
  UPLOAD_FOLDER_DOC_TECH: false,
  UPLOAD_FOLDER_DOC_REQ: false,
  UPLOAD_FOLDER_DOC_REPORT: false,
  UPLOAD_FOLDER_DOC_PROPOSAL: false,
  DELETE_FOLDER_DOC_TECH: false,
  DELETE_FOLDER_DOC_REQ: false,
  DELETE_FOLDER_DOC_REPORT: false,
  DELETE_FOLDER_DOC_PROPOSAL: false,
  PERMIT_VIEW: false,
  PERMIT_CREATE: false,
  PERMIT_EDIT: false,
  PERMIT_DELETE: false,
  PERMIT_SHARE: false,
  COLLECTION_VIEW: false,
  SURVEY_ACCESS: false,
  HR_ACCESS: false,
  HOME_ACCESS: false,
  SITE_ACCESS: false,
  ROADMAP_ACCESS: false,
  PROJECTS_ACCESS: false,
  STAKEHOLDERS_ACCESS: false,
  MAINTENANCE_ACCESS: false,
  PROCUREMENT_ACCESS: false,
  MEETING_ACCESS: false,
  // AI Data Source Permissions
  AI_DATA_EXECUTE: false,
  AI_DATA_MEETING: false,
  AI_DATA_SPECS: false,
  AI_DATA_DRAWING: false,
  AI_DATA_PERMIT: false,
  AI_DATA_COLLECTION: false,
  AI_DATA_TEAM: false,
  AI_DATA_CRM: false,
  // AI File Analyzer Permission
  AI_FILE_ANALYSIS_ACCESS: false,
  // AI Navigation Permissions
  AI_PROJECTS_ACCESS: false,
  AI_CRM_ACCESS: false,
  // AI Tab Permissions
  AI_TAB_FINANCE: false,
  AI_TAB_RESOURCES: false,
  // Library Access
  LIBRARY_ACCESS: false,
  LIBRARY_SPECS_ACCESS: false,
  LIBRARY_KEYSPECS_ACCESS: false,
  SPORT_ACCESS: false,
  SPORT_CREATE_TOURNAMENT: false,
  SPORT_CREATE_TRAINING: false,
  // Reports Module
  REPORTS_ACCESS: false,
  REPORTS_VIEW: false,
  REPORTS_CREATE: false,
  REPORTS_ADMIN: false,
};

const ADMIN_PERMISSIONS: ConsolidatedPermissions = {
  isAdmin: true,
  canCreateProjects: true,
  canAccessItems: true,
  canAccessStakeholders: true,
  canAccessSubcons: true,
  canAccessMaintenance: true,
  canAccessProcurement: true,
  canAccessDiary: true,
  canAccessSpecs: true,
  SUBCONS_ACCESS: true,
  SUBCONS_VIEW: true,
  SUBCONS_EDIT: true,
  SUBCONS_MANAGE: true,
  SUBCONS_GLOBAL_VIEW_DATE: true,
  SUBCONS_GLOBAL_VIEW_SUBCONS: true,
  SUBCONS_GLOBAL_VIEW_PO: true,
  SUBCONS_GLOBAL_EXPORT: true,
  PROCUREMENT_MANAGE_CONTRACTORS: true,
  PROCUREMENT_CREATE_ORDER: true,
  PROCUREMENT_EDIT_ORDER: true,
  PROCUREMENT_DELETE_ORDER: true,
  ITEMS_ACCESS: true,
  ITEMS_VIEW_IN_PROJECT: true,
  ITEMS_EDIT_BASIC: true,
  ITEMS_EDIT_FUNCTIONS: true,
  ITEMS_REORDER: true,
  ITEMS_DELETE: true,
  ITEMS_DUPLICATE: true,
  SPECS_ACCESS: true,
  SPECS_ITEMS_VIEW: true,
  SPECS_ITEMS_EDIT: true,
  SPECS_MANAGE_LAYERS: true,
  SPECS_DELETE_PINS: true,
  SPECS_EXPORT: true,
  EXPORT_DATA: true,
  IMPORT_DATA: true,
  BULK_EDIT: true,
  BULK_DELETE: true,
  PROJECTS_SHARE: true,
  PROJECTS_ARCHIVE: true,
  PROJECTS_DUPLICATE: true,
  PROJECTS_SET_MARK: true,
  VIEW_FOLDER_SPECS: true,
  VIEW_FOLDER_SHOP: true,
  VIEW_FOLDER_PO: true,
  VIEW_FOLDER_DOC: true,
  VIEW_FOLDER_COST: true,
  VIEW_FOLDER_BUILD: true,
  VIEW_FOLDER_DOC_CONTRACT: true,
  VIEW_FOLDER_DOC_TECH: true,
  VIEW_FOLDER_DOC_REQ: true,
  VIEW_FOLDER_DOC_REPORT: true,
  VIEW_FOLDER_DOC_PROPOSAL: true,
  UPLOAD_FOLDER_DOC_TECH: true,
  UPLOAD_FOLDER_DOC_REQ: true,
  UPLOAD_FOLDER_DOC_REPORT: true,
  UPLOAD_FOLDER_DOC_PROPOSAL: true,
  DELETE_FOLDER_DOC_TECH: true,
  DELETE_FOLDER_DOC_REQ: true,
  DELETE_FOLDER_DOC_REPORT: true,
  DELETE_FOLDER_DOC_PROPOSAL: true,
  PERMIT_VIEW: true,
  PERMIT_CREATE: true,
  PERMIT_EDIT: true,
  PERMIT_DELETE: true,
  PERMIT_SHARE: true,
  COLLECTION_VIEW: true,
  SURVEY_ACCESS: true,
  HR_ACCESS: true,
  HOME_ACCESS: true,
  SITE_ACCESS: true,
  ROADMAP_ACCESS: true,
  PROJECTS_ACCESS: true,
  STAKEHOLDERS_ACCESS: true,
  MAINTENANCE_ACCESS: true,
  PROCUREMENT_ACCESS: true,
  MEETING_ACCESS: true,
  // AI Data Source Permissions
  AI_DATA_EXECUTE: true,
  AI_DATA_MEETING: true,
  AI_DATA_SPECS: true,
  AI_DATA_DRAWING: true,
  AI_DATA_PERMIT: true,
  AI_DATA_COLLECTION: true,
  AI_DATA_TEAM: true,
  AI_DATA_CRM: true,
  // AI File Analyzer Permission
  AI_FILE_ANALYSIS_ACCESS: true,
  // AI Navigation Permissions
  AI_PROJECTS_ACCESS: true,
  AI_CRM_ACCESS: true,
  // AI Tab Permissions
  AI_TAB_FINANCE: true,
  AI_TAB_RESOURCES: true,
  // Library Access
  LIBRARY_ACCESS: true,
  LIBRARY_SPECS_ACCESS: true,
  LIBRARY_KEYSPECS_ACCESS: true,
  SPORT_ACCESS: true,
  SPORT_CREATE_TOURNAMENT: true,
  SPORT_CREATE_TRAINING: true,
  // Reports Module — admin gets all 4
  REPORTS_ACCESS: true,
  REPORTS_VIEW: true,
  REPORTS_CREATE: true,
  REPORTS_ADMIN: true,
};

import { permissionPerformanceMonitor } from './usePermissionPerformanceMonitor';

/**
 * Fetch all permissions using SECURITY DEFINER RPC
 * ✅ RLS-safe: Uses server-side function that bypasses RLS
 * ✅ Optimized: Single database call for all permissions
 * ✅ Performance monitored: Tracks metrics for optimization
 */
async function fetchConsolidatedPermissions(userId: string): Promise<ConsolidatedPermissions> {
  const startTime = permissionPerformanceMonitor.startCheck('get_consolidated_permissions');
  
  try {
    // Call RPC function (SECURITY DEFINER - bypasses RLS)
    const { data, error } = await supabase
      .rpc('get_consolidated_permissions', { p_user_id: userId });

    // ✅ SAFE DEBUG: Only log in development mode
    if (import.meta.env.DEV && error) {
      console.warn('[ConsolidatedPermissions] RPC Error:', { 
        userId: userId.substring(0, 8),
        errorDetails: { message: error.message, code: error.code }
      });
    }

    if (error) {
      console.error('[ConsolidatedPermissions] ❌ RPC Error:', error);
      throw error;
    }
    
    if (!data) {
      console.warn('[ConsolidatedPermissions] ⚠️ RPC returned null data - returning DEFAULT_PERMISSIONS');
      permissionPerformanceMonitor.endCheck('get_consolidated_permissions', startTime, false, false);
      return DEFAULT_PERMISSIONS;
    }

    const duration = permissionPerformanceMonitor.endCheck('get_consolidated_permissions', startTime, true, false);

    // Cast from Json to our interface
    const permissions = data as unknown as ConsolidatedPermissions;

    // Log performance metrics (DEV only)
    if (import.meta.env.DEV) {
      console.debug('[ConsolidatedPermissions] RPC fetched in', Math.round(duration), 'ms', {
        userId: userId.substring(0, 8),
        isAdmin: permissions.isAdmin || false
      });

      // Warn if exceeding performance budget
      if (duration > 200) {
        console.warn('[ConsolidatedPermissions] ⚠️ Exceeded budget:', Math.round(duration), 'ms > 200ms');
      }
      
      // ✅ ENHANCED DEBUG: Log key permissions for PM role debugging
      console.info('[ConsolidatedPermissions] 🔍 KEY PERMISSIONS:', {
        // High-level access flags
        canAccessStakeholders: permissions.canAccessStakeholders,
        canAccessProcurement: permissions.canAccessProcurement,
        canAccessSubcons: permissions.canAccessSubcons,
        
        // Specific permission codes for tabs
        COLLECTION_VIEW: permissions.COLLECTION_VIEW,
        SUBCONS_GLOBAL_VIEW_SUBCONS: permissions.SUBCONS_GLOBAL_VIEW_SUBCONS,
        SUBCONS_GLOBAL_VIEW_DATE: permissions.SUBCONS_GLOBAL_VIEW_DATE,
        SUBCONS_GLOBAL_VIEW_PO: permissions.SUBCONS_GLOBAL_VIEW_PO,
        HR_ACCESS: permissions.HR_ACCESS,
        SURVEY_ACCESS: permissions.SURVEY_ACCESS,
        
        // Full permissions object for reference
        _fullPermissions: permissions
      });
    }

    // Return permissions from RPC (already structured correctly)
    return permissions;

  } catch (error) {
    permissionPerformanceMonitor.endCheck('get_consolidated_permissions', startTime, false, false);
    if (import.meta.env.DEV) {
      console.error('[ConsolidatedPermissions] Error fetching permissions:', error);
    }
    // Fail safe: return no permissions
    return DEFAULT_PERMISSIONS;
  }
}

/**
 * Main hook - returns all permissions in single object
 * 
 * Usage:
 * ```tsx
 * const permissions = useConsolidatedPermissions();
 * if (permissions.canCreateProjects) { ... }
 * ```
 */
export function useConsolidatedPermissions() {
  const { user } = useAuth();
  const { isAdmin, isLoading: isAdminLoading } = useAdminCheck();

  const query = useQuery({
    queryKey: ['consolidated-permissions', user?.id],
    queryFn: () => {
      if (!user?.id) return DEFAULT_PERMISSIONS;
      return fetchConsolidatedPermissions(user.id);
    },
    enabled: !!user?.id && !isAdmin, // ✅ Skip query for admin
    staleTime: 60 * 1000, // 60 seconds - reduced for better audit responsiveness
    gcTime: 5 * 60 * 1000,   // 5 minutes - keep in cache
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    refetchOnWindowFocus: false, // Disabled to prevent burst requests on tab resume
    refetchOnMount: false,      // ✅ Keep disabled to use cached data on navigation
  });

  // ✅ SAFE DEBUG: Only log errors in development (removed verbose render-time log that caused browser crash)
  if (import.meta.env.DEV && query.error) {
    console.warn('[ConsolidatedPermissions] Query error:', query.error);
  }

  // ✅ ADMIN BYPASS: Return all permissions = true immediately
  if (isAdmin && !isAdminLoading) {
    return {
      ...query,
      data: ADMIN_PERMISSIONS,
      isLoading: false,
    };
  }

  return query;
}

/**
 * Convenience hook for checking single permission
 * Only use when you need one specific permission check
 */
export function usePermissionCheck(permission: keyof ConsolidatedPermissions): boolean {
  const { data } = useConsolidatedPermissions();
  return data?.[permission] || false;
}
